<template>
  <div class="flex flex-col">
    <h3
      class="text-title-small mb-2"
      :class="[!error && 'text-on-surface-variant', error && 'text-error']"
    >
      {{ $t('startSurvey.selectQuestionGroupSection') }}
    </h3>
    <p v-if="error" class="text-error text-body-small -mt-2 mb-1">{{ error }}</p>

    <ul class="flex w-full flex-col gap-4">
      <li v-for="template in templates" :key="template.id">
        <div class="mb-2 flex flex-row items-center py-2">
          <BaseIcon v-if="template.icon" size="24" class="text-on-surface-variant mr-2">{{
            template.icon
          }}</BaseIcon>
          <h4 class="text-title-medium text-on-surface-variant">{{ template.title }}</h4>
          <BaseTextButton
            size="XS"
            @click="toggleSelectAll(template)"
            class="text-label-large text-on-surface-variant ml-auto"
            >{{
              isSelectedComplete(template) ? $t('common.deselectAll') : $t('common.selectAll')
            }}</BaseTextButton
          >
        </div>
        <ul class="flex flex-col gap-2">
          <template
            v-for="(section, sectionIndex) in orderedSections(template)"
            :key="`${template.id}-section-${sectionIndex}`"
          >
            <li v-for="questionGroup in section.questionGroups" :key="questionGroup.id">
              <SelectableAndExpandableQuestionGroupListItem
                :question-group="displayQuestionGroup(section, questionGroup)"
                :supporting-text="section.required ? $t('survey.required') : undefined"
                :selected="section.required || isSelected(questionGroup.id)"
                :disabled="section.required"
                :expanded="expandedQuestionGroupId === questionGroup.id"
                @toggle-expanded="toggleExpanded(questionGroup.id)"
                @toggle-selected="toggleQuestionGroup(questionGroup, section.required)"
              />
            </li>
          </template>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import type {
    QuestionGroupSection,
    StructuredFeedbackTemplate,
  } from '../../../modules/survey/models/structured-feedback-template.ts'
  import type { QuestionGroup } from '@/modules/give-feedback/models/question-group.ts'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import SelectableAndExpandableQuestionGroupListItem from '../feedback-topic/SelectableAndExpandableQuestionGroupListItem.vue'

  interface Props {
    templates: StructuredFeedbackTemplate[]
    error?: string
  }

  defineProps<Props>()

  const questionGroupIds = defineModel<string[] | undefined>({ required: true })

  const expandedQuestionGroupId = ref<string | undefined>(undefined)

  const toggleExpanded = (questionGroupId: string) => {
    expandedQuestionGroupId.value =
      expandedQuestionGroupId.value === questionGroupId ? undefined : questionGroupId
  }

  const toggleQuestionGroup = (questionGroup: QuestionGroup, required: boolean) => {
    if (required) return

    if (questionGroupIds.value?.includes(questionGroup.id)) {
      questionGroupIds.value = questionGroupIds.value.filter((id) => id !== questionGroup.id)
    } else {
      questionGroupIds.value = [...(questionGroupIds.value ?? []), questionGroup.id]
    }
  }

  const isSelected = (questionGroupId: string) => {
    return !!questionGroupIds.value?.includes(questionGroupId)
  }

  const templateQuestionGroupIds = (template: StructuredFeedbackTemplate) => {
    return (
      template.questionGroupSections?.flatMap((section) =>
        section.questionGroups.map((questionGroup) => questionGroup.id),
      ) ?? []
    )
  }

  const toggleSelectAll = (template: StructuredFeedbackTemplate) => {
    const ids = templateQuestionGroupIds(template)

    if (ids.every((id) => questionGroupIds.value?.includes(id))) {
      questionGroupIds.value = questionGroupIds.value?.filter((id) => !ids.includes(id))
    } else {
      questionGroupIds.value = Array.from(new Set([...(questionGroupIds.value ?? []), ...ids]))
    }
  }

  const isSelectedComplete = (template: StructuredFeedbackTemplate) => {
    const ids = templateQuestionGroupIds(template)
    return ids.length > 0 && ids.every((id) => questionGroupIds.value?.includes(id))
  }

  const optionalSections = (template: StructuredFeedbackTemplate) => {
    return template.questionGroupSections?.filter((section) => !section.required) ?? []
  }

  const requiredSections = (template: StructuredFeedbackTemplate) => {
    return template.questionGroupSections?.filter((section) => section.required) ?? []
  }

  const orderedSections = (template: StructuredFeedbackTemplate) => {
    return [...optionalSections(template), ...requiredSections(template)]
  }

  const displayQuestionGroup = (
    section: QuestionGroupSection,
    questionGroup: QuestionGroup,
  ): QuestionGroup => {
    if (questionGroup.title || !section.title) return questionGroup
    return { ...questionGroup, title: section.title }
  }
</script>
